import { useId } from 'react';
import { cn } from '../utils/cn';

export interface SparklineProps {
  points: Array<number | null>;
  width?: number;
  height?: number;
  /** Token-bound stroke, e.g. `var(--color-chart-1)`. Never a literal hex. */
  stroke?: string;
  filled?: boolean;
  /** Sentence read by assistive technology in place of the drawing. */
  accessibleSummary?: string;
  className?: string;
}

/**
 * A trend line small enough to sit inside a KPI tile. Nulls break the line
 * rather than being interpolated — a gap in the data is information.
 */
export function Sparkline({
  points,
  width = 96,
  height = 28,
  stroke = 'var(--color-chart-1)',
  filled = false,
  accessibleSummary,
  className,
}: SparklineProps) {
  const gradientId = useId();
  const known = points.filter((p): p is number => p !== null);
  if (known.length < 2) {
    return (
      <div
        className={cn('flex items-center text-micro text-content-muted', className)}
        style={{ width, height }}
      >
        No trend
      </div>
    );
  }

  const min = Math.min(...known);
  const max = Math.max(...known);
  const span = max - min || 1;
  const stepX = width / Math.max(points.length - 1, 1);
  const y = (value: number) => height - 2 - ((value - min) / span) * (height - 4);

  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((value, index) => {
    if (value === null) {
      if (current.length > 1) segments.push(current.join(' '));
      current = [];
      return;
    }
    current.push(
      `${current.length === 0 ? 'M' : 'L'}${(index * stepX).toFixed(2)},${y(value).toFixed(2)}`,
    );
  });
  if (current.length > 1) segments.push(current.join(' '));

  const areaPath =
    filled && segments.length === 1 ? `${segments[0]} L${width},${height} L0,${height} Z` : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      role={accessibleSummary ? 'img' : undefined}
      aria-label={accessibleSummary}
      aria-hidden={accessibleSummary ? undefined : true}
      preserveAspectRatio="none"
    >
      {areaPath ? (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
        </>
      ) : null}
      {segments.map((segment, index) => (
        <path
          key={index}
          d={segment}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}
